import {
    extractEntryPointData,
    getBrowserName,
    getBrowserVersion,
    getClientTimestamp,
    getDeviceModel,
    getDeviceType,
    getDeviceVendor,
    getOSName,
    getOSVersion,
    getSessionId,
    getSystemLocale,
    getTimeZone,
    getTimeZoneName,
    Softgames,
} from '@softgames/event-tracking';
import Avo, {
    AdNetwork,
    AdNetworkValueType,
    AdResponse,
    AdResponseValueType,
    AdType,
    AdTypeValueType,
    AvoEnv,
    LevelResultValueType,
    PaymentProvider,
    PurchaseResponse,
    PurchaseResponseValueType,
} from './Avo';
import { AdvertisementType, IAdvertisementPayload, IAdvertisementPlacement } from 'Utility/SDK/Advertisement';
import Environment from 'Utility/Environment';
import { getGameConfig } from '@platform/GameConfig';
import { IGameConfig } from 'Utility/IGameConfig';
import { Platform } from 'Utility/Platform';
import Game from 'Game';
import { IGameState } from 'State/Game/IGameState';
import { getDurationSinceJSEnabled } from '../TrackingHelpers/DurationSinceJSEnabled';
import { ProductId } from 'Utility/Product';
import { IAPDebugMode } from 'State/Game/IAPDebugMode';
import { getTrackingDataSet } from '../Track';
import { getStoreEntryPoint } from '../TrackingHelpers/StoreEntryPoint';
import { PurchaseStatus } from '../TrackingHelpers/PurchaseStatus';
import { IAPProduct } from 'Utility/SDK/Drivers/IAPCatalog';
import { parseCurrency } from 'Utility/Currency';
import { Logger, LogThresholds } from 'Utility/Logger/Logger';
import { LogOrigin } from 'Utility/Logger/LogOrigin';
import { LogStyles } from 'Utility/Logger/LogStyles';
import { AdType as TrackingAdType } from '../DataSet/Ad';
import { Random } from 'Utility/Random';
import { getLevelMetricsCount, getLevelResourceUpdates, getResourceActionType, getResourceInventory, getResourceUpdates } from './ResourceHelper';

const EVENT_TRACKING_ENABLED = false;
const GAME_SLUG = 'solitaire-farm-seasons';
const SOFTGAMES_API_KEY = 'DxLPnLDttE8iU0sdwxW43adY7BKfXjQD1nm7Wcda';

let levelSessionID = '';

export interface AdEventProperties {
    id: string;
    network: AdNetworkValueType;
    type: AdvertisementType;
    payload: IAdvertisementPayload;
}

export class AvoTracking {
    private static gameConfig: IGameConfig;

    /**
     * Initialize event tracking, call as early as possible, and before attempting
     * to call any of the send* functions.
     */
    public static async init() {
        if (!EVENT_TRACKING_ENABLED) {
            return;
        }
        const isProd = process?.env.NODE_ENV === Environment.PRODUCTION;
        AvoTracking.gameConfig = await getGameConfig();
        Avo.initAvo(
            // Avo setup options
            {
                env: isProd ? Avo.AvoEnv.Prod : Avo.AvoEnv.Dev,
                webDebugger: true,
                avoLogger: {
                    logDebug: (_: AvoEnv, message: string): boolean => logAvo(message, LogStyles.VISIBLE),
                    logWarn: (_: AvoEnv, message: string): boolean => logAvo(message, LogStyles.WARNING),
                    logError: (_: AvoEnv, message: string): boolean => logAvo(message, LogStyles.ERROR),
                }
            },
            // system properties:
            {
                sessionId: getSessionId(),
                gameVersion: AvoTracking.gameConfig.version,
                gameSlug: GAME_SLUG,
                platformName: 'facebook',
                environment: isProd ? 'prod' : 'dev',
                schemaVersion: '65',
            },
            // destination options are unused
            {},
            // Softgames destination
            new Softgames.Destination({
                apiKey: SOFTGAMES_API_KEY,
                debugLog: true,
            })
        );
    }

    /**
     * Sends the 'Game Initialized' event, this should be called after FBInstant.startGameAsync()
     * has resolved (or at a similar place).
     */
    public static async gameInitialized(state: IGameState) {
        if (!EVENT_TRACKING_ENABLED) {
            return;
        }
        const extractedEntryPointData = await AvoTracking.getExtractedEntryPointData();
        const platformUserId = Game.sdk.getUserId();

        let maxTextureSize = null;
        try {
            const gl = document.createElement('canvas').getContext('webgl');
            maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
        } catch (e) {
            console.warn('web gl unsupported');
        }

        Avo.gameInitialized({
            browserName: getBrowserName(),
            browserVersion: getBrowserVersion(),
            systemLocale: getSystemLocale(),
            osName: getOSName(),
            osVersion: getOSVersion(),
            timeZoneName: getTimeZoneName(),
            timeZone: getTimeZone(),
            deviceType: getDeviceType(),
            deviceVendor: getDeviceVendor(),
            deviceModel: getDeviceModel(),
            timeSinceStartup: getDurationSinceJSEnabled() / 1000,
            backendName: 'softgames',
            backendUserId: Game.sdk.getGBUserId(),
            profileId: state.userState.userId,
            newPlayer: state.playerState.FTUX.length === 0,
            sessionCounter: state.playerState.sessionCount,
            entryPointName: extractedEntryPointData.name,
            entryPointData: extractedEntryPointData.data,
            platformUserId,
            clientTimestamp: getClientTimestamp(),
            userFingerprint: null,
            maxTextureSize,
            metaProperties: [],
            affiliateId: null,
        });
    }

    /**
     * Sends the 'Ad Finished' event, takes an AdFinishedProperty object information
     * about the ad. This function should be called right after an ad is shown.
     *
     * @param props
     * @param props.id a unique id which is the same for all events of the same ad
     * @param props.placement the placement data object
     * @param props.payload the IAdvertisementPayload object
     */
    public static async adFinished(
        state: IGameState,
        id: string,
        placement: IAdvertisementPlacement,
        payload: IAdvertisementPayload
    ) {
        if (!EVENT_TRACKING_ENABLED) {
            return;
        }
        Avo.adFinished({
            adPlacementId: placement.id,
            adSlotName: payload.g_gameArea,
            adId: id,
            adType: getAdTypeFromPlacement(placement),
            adNetwork: getAdNetwork(),
            clientTimestamp: getClientTimestamp(),
            adResponse: getAdResponse(placement, payload),
            errorMessage: payload.error,
            countRetries: payload.retries,
            adShowDelay: (Date.now() - payload.adTimestamp) / 1000,
            metaProperties: [],
        });
    }

    /**
     * Sends the 'Purchase Finished' event, takes a PurchaseFinishedProperties object information
     * about the purchase. This function should be called right after an purchase is completed.
     *
     * @param props
     * @param props.state the current game state
     * @param props.productId the product id of the purchased product (exactly 'productId' but not 'offerId' because
     * 'productId' is an id of a platform and 'offerId' is an internal id within the game).
     * @param props.status the status of the purchase
     * @param props.type the type of purchase
     */
    public static async purchaseFinished(
        state: IGameState,
        productId: ProductId,
        transactionReceipt: string,
        errorMessage?: string
    ): Promise<void> {
        if (!EVENT_TRACKING_ENABLED) {
            return;
        }
        const catalog: IAPProduct[] = Game.sdk.getCatalog().products;
        const product: IAPProduct = catalog.find((productData: IAPProduct) => productData.productId === productId);
        const params = getTrackingDataSet();
        Avo.purchaseFinished({
            productId,
            clientTimestamp: getClientTimestamp(),
            sandboxPurchase: state.perSessionData.iapDebugMode !== IAPDebugMode.DEFAULT,
            currencyCode: product.priceCurrencyCode || 'failed_to_find',
            gameArea: params.tGameArea,
            paymentProvider: PaymentProvider.FACEBOOK,
            productPrice: product.price,
            purchaseResponse: getPurchaseResponse(params.tPurchaseStatus),
            errorMessage,
            storeEntryPoint: getStoreEntryPoint(state),
            purchaseReceipt: transactionReceipt,
            transactionId: params.transactionID,
            productPriceAmount: parseCurrency(product.price).value ?? 0.0,
            productType: 'Consumable',
            metaProperties: [],
        });
    }

    /**
     * Sends the 'Cheats Enabled' event.
     * This function should be called right after cheats are enabled in the game.
     */
    public static async cheatsEnabled() {
        // Avo.cheatsEnabled({ clientTimestamp: getClientTimestamp(), metaProperties: [] });
    }


    public static async resourceAction(state: IGameState) {
        if (!EVENT_TRACKING_ENABLED) {
            return;
        }
        const params = getTrackingDataSet();
        Avo.sofaResourceAction({
            clientTimestamp: getClientTimestamp(),
            sofaResourceUpdates: getResourceUpdates(params),
            sofaResourceInventory: getResourceInventory(state),
            gameArea: params.tGameArea,
            levelNumber: params.mLevel,
            levelType: params.mIsHardLevel ? 'hard' : params.mIsPlinkoLevel ? 'plinko' : 'normal',
            resourceActionTrigger: params.tActionType,
            resourceActionType: getResourceActionType(params),
            metaProperties: [],
        });
    }

        /**
     * Send the Level Started Event.
     * This function should be called right after a level was started
     */
    public static async levelStarted(state: IGameState) {
        if (!EVENT_TRACKING_ENABLED) {
            return;
        }
        levelSessionID = Random.ID();
        const params = getTrackingDataSet();
        Avo.sofaLevelStarted({
            clientTimestamp: getClientTimestamp(),
            levelNumber: params.mLevel,
            levelId: `level_${params.mLevel}`,
            levelMoves: params.mLevelMoves,
            levelGoal: params.mLevelType,
            levelType: params.mIsHardLevel ? 'hard' : params.mIsPlinkoLevel ? 'plinko' : 'normal',
            levelAttemptId: levelSessionID,
            eventContextSlug: params.missionID,
            metaProperties: [],
        });
    }
    
    public static async levelFinished(state: IGameState, levelResult: LevelResultValueType) {
        if (!EVENT_TRACKING_ENABLED) {
            return;
        }
        const params = getTrackingDataSet();
        Avo.sofaLevelFinished({
            levelNumber: params.mLevel,
            levelId: `level_${params.mLevel}`,
            levelGoal: params.mLevelType,
            levelType: params.mIsHardLevel ? 'hard' : params.mIsPlinkoLevel ? 'plinko' : 'normal',
            levelAttemptId: levelSessionID,
            levelScore: params.mScore,
            clientTimestamp: getClientTimestamp(),
            eventContextSlug: params.missionID,
            levelResult,
            sofaLevelMetricsCount: getLevelMetricsCount(params),
            sofaResourceInventory: getResourceInventory(state),
            sofaResourceUpdates: getLevelResourceUpdates(params),
            metaProperties: [],
        });
    }
    

    private static async getExtractedEntryPointData() {
        let fbEntryPoint;
        let fbEntryPointData;
        if (AvoTracking.gameConfig.platform === Platform.FACEBOOK) {
            try {
                fbEntryPoint = await FBInstant.getEntryPointAsync();
                fbEntryPointData = FBInstant.getEntryPointData();
            } catch (err) {
                console.warn(err);
            }
        }
        const data = extractEntryPointData(fbEntryPoint, fbEntryPointData);
        return data;
    }
}

function getAdNetwork(): AdNetworkValueType {
    const params = getTrackingDataSet();
    const adNetwork = params.adType || TrackingAdType.FAKE_AD;
    switch (adNetwork)
    {
        case TrackingAdType.INTERSTITIAL:
        case TrackingAdType.REWARDED:
            return AdNetwork.FACEBOOK;
        case TrackingAdType.REWARDED_CROSS_PROMO:
        case TrackingAdType.FAKE_AD:
        default:
            return AdNetwork.SOFTGAMES;
    }
}

function getAdResponse(placement: IAdvertisementPlacement, payload: IAdvertisementPayload): AdResponseValueType {
    if (payload.successfully) return AdResponse.SUCCESS;
    return payload.wasCanceled ? AdResponse.CLOSED : AdResponse.ERROR;
}

function getAdTypeFromPlacement(placement: IAdvertisementPlacement): AdTypeValueType {
    switch (placement.type) {
        case AdvertisementType.INTERSTITIAL:
            return AdType.INTERSTITIAL;
        case AdvertisementType.REWARDED:
        case AdvertisementType.WATCH_TO_PLAY:
            return AdType.REWARDED;
    }
}


function getPurchaseResponse(status: PurchaseStatus): PurchaseResponseValueType {
    return status === PurchaseStatus.FAIL || status === PurchaseStatus.ERROR ? PurchaseResponse.ERROR : PurchaseResponse.SUCCESS;
}

function logAvo(message: string, style: LogStyles): boolean {
    Logger.log(message, { origin: LogOrigin.AVO, style, threshold: LogThresholds.HIGH });

    if (style === LogStyles.VISIBLE) {
        try {
            let formattedMessage = message.split("User Props:");
            const userProperties = JSON.parse(formattedMessage[1]);
            formattedMessage = formattedMessage[0].split("Event Props:");
            const eventProperties = JSON.parse(formattedMessage[1]);
            formattedMessage = formattedMessage[0].split("Event Sent:");
            const eventName = formattedMessage[1];
            console.log("[avo] ", { eventName, eventProperties, userProperties })
        }
        catch (e) {
            console.log("[avo] Error parsing Avo log");
        }
    }
    return true;
}